import { computed, ref } from 'vue';
import { listEvents, nextEventsPage } from '@/js/core/utils/events-api';
import { DEFAULT_UPCOMING_DAYS, displayEventRows } from '@/js/core/utils/digital-display-events';
import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone';

const EVENTS_PER_REQUEST = 100;

/* Used until the endpoint answers, and whenever it sends something unusable. */
const DEFAULT_ROWS_PER_SLIDE = 10;
const DEFAULT_SECONDS_PER_SLIDE = 60;
const DEFAULT_PAGE_TITLE = 'Upcoming Events';

/**
 * Owns the display's events: fetching, the slide settings that come back with them, and the rows
 * currently on the board.
 *
 * Deliberately not the shared events store. That store keeps only `meta.pagination`, dropping the
 * settings the display needs, and caches by request key so a second call never reaches the
 * network — which would make the per-cycle refresh a no-op. Fetching here also keeps the display
 * off a store the other calendars depend on.
 *
 * @param {{ timezone?: string }} options
 */
export function useDigitalDisplayEvents({ timezone = DEFAULT_TIMEZONE } = {}) {
  const items = ref([]);
  const rowsPerSlide = ref(DEFAULT_ROWS_PER_SLIDE);
  const secondsPerSlide = ref(DEFAULT_SECONDS_PER_SLIDE);
  const upcomingDays = ref(DEFAULT_UPCOMING_DAYS);
  const pageTitle = ref(DEFAULT_PAGE_TITLE);
  const isLoading = ref(true);
  const fetchError = ref('');
  const slideIndex = ref(0);
  let events = [];
  let slideTimer = null;
  let isCycling = false;

  /**
   * Rows cut into slides of at most rowsPerSlide, the remainder left on the last one. A date group
   * running past a boundary carries on at the top of the next slide, showing its date again.
   */
  const slides = computed(() => {
    const size = rowsPerSlide.value;
    const chunks = [];

    for (let start = 0; start < items.value.length; start += size) {
      chunks.push(items.value.slice(start, start + size));
    }

    return chunks;
  });

  const slideCount = computed(() => slides.value.length);
  const currentSlide = computed(() => slides.value[slideIndex.value] || []);
  /* Only surface a failure when there is nothing else to show: a refetch runs every cycle, and one
     bad request must not wipe a working board. */
  const error = computed(() => (items.value.length === 0 ? fetchError.value : ''));

  function rebuildItems() {
    items.value = displayEventRows(events, timezone, Date.now(), {
      upcomingDays: upcomingDays.value,
    });

    if (slideIndex.value >= slideCount.value) {
      slideIndex.value = 0;
    }
  }

  function startSlideCycle() {
    stopSlideCycle();
    isCycling = true;
    scheduleNextSlide();
  }

  function stopSlideCycle() {
    isCycling = false;
    clearTimeout(slideTimer);
    slideTimer = null;
  }

  /* One timeout per slide that resets itself */
  function scheduleNextSlide() {
    slideTimer = setTimeout(async () => {
      await showNextSlide();

      if (isCycling) {
        scheduleNextSlide();
      }
    }, secondsPerSlide.value * 1000);
  }

  /* Wraps the last slide back to the first, awaiting a refetch on the way round before slide 1 shows */
  async function showNextSlide() {
    const nextIndex = slideIndex.value + 1;

    if (nextIndex >= slideCount.value) {
      await refresh({ resetToFirstSlide: true });
      return;
    }

    slideIndex.value = nextIndex;
  }

  /**
   * Fetch every page, then rebuild. A failed request leaves the previous events untouched, so the
   * board keeps showing the last good data instead of emptying.
   */
  async function refresh({ resetToFirstSlide = false } = {}) {
    let result = null;
    let message = '';

    try {
      result = await fetchAllEvents();
    } catch (err) {
      console.error('API Error:', err);
      message = 'Could not load events.';
    }

    if (result) {
      events = result.events;
      applyDisplaySettings(result.meta);
    }

    fetchError.value = message;
    isLoading.value = false;
    rebuildItems();

    if (resetToFirstSlide) {
      slideIndex.value = 0;
    }
  }

  async function fetchAllEvents() {
    const collected = [];
    let meta = null;
    let page = 1;

    const accessKeyMatch = window.location.pathname.match(
      /\/calendar\/digital-display\/key\/([^/]+)/
    );

    while (page) {
      // The PSK will join these params once the endpoint expects it.
      const response = await listEvents(
        { per_page: EVENTS_PER_REQUEST, page },
        'displays',
        accessKeyMatch ? decodeURIComponent(accessKeyMatch[1]) : null
      );

      collected.push(...(response.data || []));
      meta = response.meta;
      page = nextEventsPage(response.meta?.pagination, page);
    }

    return {
      events: [...new Map(collected.map(event => [event.id, event])).values()],
      meta,
    };
  }

  function applyDisplaySettings(meta) {
    rowsPerSlide.value = positiveInteger(meta?.rows_per_slide, DEFAULT_ROWS_PER_SLIDE);
    secondsPerSlide.value = positiveInteger(meta?.time_per_slide, DEFAULT_SECONDS_PER_SLIDE);
    upcomingDays.value = positiveInteger(meta?.upcoming_days, DEFAULT_UPCOMING_DAYS);
    pageTitle.value = nonEmptyString(meta?.page_title, DEFAULT_PAGE_TITLE);
  }

  return {
    items,
    slides,
    slideCount,
    slideIndex,
    currentSlide,
    rowsPerSlide,
    secondsPerSlide,
    upcomingDays,
    pageTitle,
    isLoading,
    error,
    refresh,
    rebuildItems,
    startSlideCycle,
    stopSlideCycle,
  };
}

/* A zero or a negative would stall or thrash the slide timer, so both fall back. */
function positiveInteger(value, fallback) {
  const number = Number(value);

  return Number.isFinite(number) && number > 0 ? Math.floor(number) : fallback;
}

/* An empty title would leave the board with a blank heading, so it falls back too. */
function nonEmptyString(value, fallback) {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}
